import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import { SITE_SECTION_TITLE_MAX_LENGTH, SITE_SECTION_TITLE_MIN_LENGTH } from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    Fieldset,
    Form,
    Label,
    Submit,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient } from '../../../state';
import { DraftBadge } from './DraftBadge';
import { SiteStructureItemList } from './SiteStructureItemList';
import { SpaceGitSyncSiteOverrideAlert } from './SpaceGitSyncSiteOverrideAlert';
import { TitleAndIconField } from './TitleAndIconField';

type SectionFormData = {
    title: string;
    icon: string | null;
};

export interface CreateSiteSectionFormProps {
    organization: api.Organization;
    site: api.Site;
    inSiteSectionGroup?: api.SiteSectionGroup | null;
    /** Existing space to link, or `null` to create and link a space on submission. */
    space: api.Space | null;
    /** Template for the new space when `space` is `null`. */
    template?: api.SpaceTemplate | null;
    pageId?: string | null;
    /** Title of the resolved page when `pageId` is set, combined with the space title to form the section's default title. */
    defaultTitle?: string;
    onCreateSection?: (siteSection: api.SiteSection) => void;
}

export function CreateSiteSectionForm(props: CreateSiteSectionFormProps) {
    const {
        organization,
        site,
        inSiteSectionGroup,
        space,
        template,
        pageId,
        defaultTitle,
        onCreateSection,
    } = props;

    const organizationId = organization.id;
    const form = useForm<SectionFormData>({
        defaultValues: {
            title:
                space && pageId && defaultTitle
                    ? `${space.title} - ${defaultTitle}`
                    : (space?.title ?? template?.title ?? 'Untitled'),
            icon: null,
        },
    });

    const [isPending, startDialogAction] = useDialogAction();
    const onSubmit: SubmitHandler<SectionFormData> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const { data: siteSection } = await apiClient.siteSections.addSectionToSite(
                    organizationId,
                    site.id,
                    {
                        spaceId: space?.id ?? null,
                        ...(template ? { template: { id: template.id } } : {}),
                        draft: true,
                        title: data.title,
                        icon: data.icon,
                        ...(pageId ? { pageId } : {}),
                        ...(inSiteSectionGroup
                            ? { siteSectionGroupId: inSiteSectionGroup.id }
                            : {}),
                    }
                );
                if (!space) {
                    const createdSpace = siteSection.siteSpaces[0]?.space;
                    if (createdSpace) {
                        analytics.logEvent(
                            analytics.createSpaceEvent(createdSpace, {
                                type: 'space_create',
                                source: template ? 'template' : 'blank',
                            })
                        );
                    }
                    if (template) {
                        analytics.logEvent({
                            type: 'templates_pick_template',
                            template: template.id,
                            organization: organizationId,
                        });
                    }
                }
                onCreateSection?.(siteSection);
            }, 'Error while adding section');
            close();
        });
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <Fieldset>
                    <TitleAndIconField
                        form={form}
                        titleName="title"
                        iconName="icon"
                        minLength={SITE_SECTION_TITLE_MIN_LENGTH}
                        maxLength={SITE_SECTION_TITLE_MAX_LENGTH}
                        disabled={isPending}
                        autoFocus
                        titleSuffix={<DraftBadge />}
                        titleInputFooter={
                            <div className="text-ui-small text-muted">
                                <Icon.InfoCircle className="-mt-0.5 mr-1.5 inline-block shrink-0" />
                                Sections start as drafts and aren't visible on your published site
                                until undrafted.
                            </div>
                        }
                    >
                        <Label>Title & Icon</Label>
                        <Description>
                            Choose a title and icon that represents this section.
                        </Description>
                    </TitleAndIconField>

                    {inSiteSectionGroup ? (
                        <SiteStructureItemList
                            description="The section will be added to this group:"
                            items={[inSiteSectionGroup]}
                        />
                    ) : null}
                </Fieldset>
                {space ? (
                    <SpaceGitSyncSiteOverrideAlert space={space} site={site} className="mt-6" />
                ) : null}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Add section" disabled={isPending} />
            </DialogFooter>
        </Form>
    );
}
